/**
 * PDF renderer.
 *
 * Asks the source for an inline-rendering URL (`getBlobUrl({ inline: true })`)
 * and hands it to an iframe. The `inline: true` hint is the load-bearing
 * piece — without it, cross-origin Spaces / S3 responses with no
 * `Content-Disposition: inline` header would cause Chromium / Edge to
 * download the file instead of rendering it (see issue #989).
 *
 * Source adapters fulfil that hint per-backend: `urlSource` hits the
 * `/api/files/attachments/:id/preview-url` re-sign endpoint when a
 * `fileId` is available; `artifactSource` constructs a blob URL with the
 * right MIME on the fly.
 */
import { Loader2 } from "lucide-react";

import { useFileBlobUrl } from "../hooks/use-file-blob-url";
import type { FileRendererProps } from "../types";

export function PdfRenderer({ source }: FileRendererProps) {
  const state = useFileBlobUrl(source, { inline: true });

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p>Unable to preview this PDF.</p>
        <p className="text-xs opacity-70">{state.error.message}</p>
      </div>
    );
  }

  return (
    <iframe
      src={state.url}
      title={source.filename}
      className="w-full h-full border-0"
    />
  );
}
